import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import API from "../../config/axios";
import { api_paths } from "../../config/api.paths";
import DatePicker from "../common/DatePicker";
import SelectField from "../common/SelectField";
import { validateData, clearError } from "../../validators/YupValidations";
import { memberSchema } from "../../validators/validatorSchema";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";

const Field = ({ label, required, children, error }) => (
  <div className="mb-3 pb-1">
    <label className="form-label small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
);

const MemberModal = ({ show, onHide, editData, refreshList }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: null,
    gender: "",
    relationship_id: "",
    marital_status: null,
    phone: "",
    email: "",
    country_code: editData?.country_code || "+1",
  });

  const [errors, setErrors] = useState({});
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(false);

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  useEffect(() => {
    const loadRelationships = async () => {
      try {
        const res = await API.get(
          api_paths.member.family_member.GET_RELATIONSHIP
        );
        setRelationships(res.data?.payload?.result || []);
      } catch (err) {
        console.log("Failed to load relationships", err);
      }
    };
    loadRelationships();
  }, []);

  useEffect(() => {
    if (show) {
      // Reset form when modal is closed
      if (editData) {
        setForm({
          first_name: editData.first_name || "",
          last_name: editData.last_name || "",
          date_of_birth: editData.date_of_birth
            ? new Date(editData.date_of_birth)
            : null,
          gender: editData.gender || "",
          relationship_id: editData.relationship_id || "",
          marital_status: editData.marital_status || null,
          phone: editData.phone || "",
          email: editData.email || "",
          country_code: editData.country_code || "+1",
        });
      } else {
        setForm({
          first_name: "",
          last_name: "",
          date_of_birth: null,
          gender: "",
          relationship_id: "",
          marital_status: null,
          phone: "",
          email: "",
          country_code: "+1",
        });
      }
      setErrors({});
    }
  }, [show, editData]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) clearError(setErrors, field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(memberSchema, form, setErrors);
    if (!isValid) return;

    try {
      setLoading(true);
      const payload = {
        ...form,
        gender: form.gender.toLowerCase(),
        marital_status: form?.marital_status?.toLowerCase() || null,
      };

      if (editData?._id) {
        await API.put(
          `${api_paths.member.family_member.UPDATE_MEMBER}/${editData._id}`,
          payload
        );
        toastSuccessMessage("Member updated successfully");
      } else {
        await API.post(api_paths.member.family_member.ADD_MEMBER, payload);
        toastSuccessMessage("Member added successfully");
      }

      refreshList();
      onHide();
    } catch (error) {
      console.log("Error saving member:", error);
      toastErrorMessage("Something went wrong while saving the member.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editData ? "Edit Family Member" : "Add Family Member"}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body className="px-4 mx-1">
          <div className="row">
            <div className="col-md-6">
              <Field
                label="First Name"
                required
                error={errors.first_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${
                    errors.first_name ? "is-invalid" : ""
                  }`}
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field
                label="Last Name"
                required
                error={errors.last_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${
                    errors.last_name ? "is-invalid" : ""
                  }`}
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Enter last name"
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Date of Birth" required>
                <DatePicker
                  value={form.date_of_birth}
                  onChange={(date) => handleChange("date_of_birth", date)}
                  error={errors.date_of_birth?.message}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Gender" required error={errors.gender?.message}>
                <SelectField
                  value={
                    genderOptions.find((o) => o.value === form.gender) || null
                  }
                  options={genderOptions}
                  onChange={(opt) => handleChange("gender", opt.value)}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field
                label="Relationship"
                required
                error={errors.relationship_id?.message}
              >
                <SelectField
                  value={
                    relationships
                      .map((r) => ({ value: r._id, label: r.name }))
                      .find((o) => o.value === form.relationship_id) || null
                  }
                  options={relationships.map((r) => ({
                    value: r._id,
                    label: r.name,
                  }))}
                  onChange={(opt) => handleChange("relationship_id", opt.value)}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field
                label="Marital Status"
                error={errors.marital_status?.message}
              >
                <SelectField
                  value={
                    maritalStatusOptions.find(
                      (o) => o.value === form.marital_status
                    ) || null
                  }
                  options={maritalStatusOptions}
                  onChange={(opt) => handleChange("marital_status", opt.value)}
                />
              </Field>
            </div>

            <div className="col-12 col-md-6">
              <Field label="Mobile Number">
                <div className="d-flex gap-2">
                  {/* Country Code Select */}
                  <div style={{ width: "120px" }}>
                    <SelectField
                      value={countryCodeOptions.find(
                        (opt) => opt.value === form.country_code
                      )}
                      options={countryCodeOptions}
                      onChange={(opt) =>
                        handleChange("country_code", opt.value)
                      }
                    />
                  </div>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    placeholder="Enter Mobile Number"
                    className={`form-control form-control-sm ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    maxLength={10}
                  />
                </div>

                {errors.phone && (
                  <div className="invalid-feedback d-block">
                    {errors.phone.message}
                  </div>
                )}
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Email" error={errors.email?.message}>
                <input
                  type="email"
                  className={`form-control form-control-sm ${
                    errors.email ? "is-invalid" : ""
                  }`}
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter email"
                />
              </Field>
            </div>
          </div>

          <Modal.Footer className="d-flex justify-content-start  border-top-0 p-0 my-3">
            <Button
              type="submit"
              className="btn btn-orange-md m-0"
              disabled={loading}
            >
              {loading ? "Saving..." : editData ? "Save Changes" : "Add Member"}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </form>
    </Modal>
  );
};

export default MemberModal;
